import {Box, Center, Group, Table, Text} from '@mantine/core'
import {Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts'

import type {
    CapacityBucket,
    CapacityEngagementMatrixData,
    EngagementLevel,
} from 'src/api/client/models/analytics'
import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import type {ExportKind} from 'src/components/export-image-button'
import {SectionCard} from 'src/components/section-card'
import {ENGAGEMENT_CHART_COLORS} from 'src/lib/engagement-colors'
import {formatNumber, formatPercent} from 'src/lib/format'

const ENGAGEMENT_LEVELS = [
    'Very Engaged',
    'Engaged',
    'Somewhat Engaged',
    'Minimally Engaged',
    'Disengaged',
] as const

/** Vertical legend on the left; pie centered in the remaining width of the (narrow) chart. */
const PIE_CHART_MARGIN = {top: 24, right: 12, bottom: 28, left: 12}
const PIE_CHART_MAX_WIDTH = 480
const PIE_OUTER_RADIUS = 100
const PIE_SIDE_BY_SIDE_OUTER_RADIUS = 120

function engagementPercentLabel(count: number, grandTotal: number): string {
    if (grandTotal <= 0) {
        return '0%'
    }
    return formatPercent(count / grandTotal)
}

/** Matches Recharts `DefaultTooltipContent` wrapper so custom tooltip content gets the same box. */
const PIE_TOOLTIP_BOX_STYLE = {
    margin: 0,
    padding: 10,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    whiteSpace: 'nowrap' as const,
}

const MAX_COLUMN_WIDTH_BY_ENGAGEMENT_LEVEL = {
    'Very Engaged': 130,
    Engaged: 100,
    'Somewhat Engaged': 150,
    'Minimally Engaged': 150,
    Disengaged: 120,
}

const shouldHighlightCell = ({
    engagementLevel,
    capacityBucket,
}: {
    engagementLevel?: EngagementLevel
    capacityBucket?: CapacityBucket
}) => {
    if (
        (engagementLevel === 'Very Engaged' || engagementLevel === 'Engaged') &&
        (capacityBucket === '$1,000,000+' ||
            capacityBucket === '$500,000 to $999,999' ||
            capacityBucket === '$250,000 to $499,999' ||
            capacityBucket === '$100,000 to $249,999')
    ) {
        return true
    }
    if (
        engagementLevel === 'Somewhat Engaged' &&
        (capacityBucket === '$1,000,000+' || capacityBucket === '$500,000 to $999,999')
    ) {
        return true
    }
    return false
}

type Props = {
    data: CapacityEngagementMatrixData
    title: string
    onProspectFilter?: (filters: ProspectClickFilters) => void
}

type ChartTableLayout = 'stack' | 'sideBySide'
type ChartTableContent = 'chart' | 'table' | 'both'

function TableCell({
    engagementLevel,
    capacityBucket,
    value,
    onClick,
    style,
}: {
    engagementLevel?: EngagementLevel
    capacityBucket?: CapacityBucket
    value: number
    onClick?: () => void
    style?: React.CSSProperties
}) {
    const isHighlighted =
        capacityBucket && engagementLevel && shouldHighlightCell({engagementLevel, capacityBucket})
    const isBold = !capacityBucket || !engagementLevel
    return (
        <Table.Td
            className={onClick ? 'capacity-matrix-clickable-cell' : undefined}
            onClick={onClick}
            style={{
                textAlign: 'right',
                backgroundColor: isHighlighted ? 'var(--mantine-color-green-1)' : '',
                fontWeight: isBold ? 'bold' : undefined,
                border: isHighlighted ? '1px solid var(--mantine-color-green-3)' : 'none',
                cursor: onClick ? 'pointer' : 'default',
                ...style,
            }}
        >
            {formatNumber(value)}
        </Table.Td>
    )
}

function CapacityEngagementMatrixChartAndTable({
    data,
    onProspectFilter,
    layout = 'stack',
    content = 'both',
}: {
    data: CapacityEngagementMatrixData
    onProspectFilter?: (filters: ProspectClickFilters) => void
    layout?: ChartTableLayout
    content?: ChartTableContent
}) {
    const pieData = ENGAGEMENT_LEVELS.map((eng) => ({
        name: eng,
        value: data.rows.reduce((sum, row) => sum + (row[eng] || 0), 0),
    }))
    const pieGrandTotal = data.grandTotal

    const chartHeight = 240

    const pieChart = (
        <Box className="capacity-engagement-pie-wrap" w="100%" maw={PIE_CHART_MAX_WIDTH} mx="auto">
            <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart margin={PIE_CHART_MARGIN} style={{overflow: 'visible'}}>
                    <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="60%"
                        cy="50%"
                        outerRadius={
                            layout === 'sideBySide'
                                ? PIE_SIDE_BY_SIDE_OUTER_RADIUS
                                : PIE_OUTER_RADIUS
                        }
                        isAnimationActive={false}
                        onClick={
                            onProspectFilter
                                ? (entry: {name?: string}) => {
                                      if (entry?.name)
                                          onProspectFilter({engagementLevel: entry.name})
                                  }
                                : undefined
                        }
                        style={onProspectFilter ? {cursor: 'pointer'} : undefined}
                        labelLine={false}
                    >
                        {pieData.map((entry) => (
                            <Cell
                                key={entry.name}
                                fill={ENGAGEMENT_CHART_COLORS[entry.name as EngagementLevel]}
                            />
                        ))}
                    </Pie>
                    <Tooltip
                        content={({active, payload, contentStyle, labelStyle}) => {
                            if (!active || !payload?.[0]) {
                                return null
                            }
                            const item = payload[0]
                            const name =
                                typeof item.name === 'string' ? item.name : String(item.name)
                            const raw = item.value
                            const value = typeof raw === 'number' ? raw : Number(raw)
                            const safeValue = Number.isFinite(value) ? value : 0
                            const pct = engagementPercentLabel(safeValue, pieGrandTotal)
                            return (
                                <div
                                    className="recharts-default-tooltip"
                                    style={{...PIE_TOOLTIP_BOX_STYLE, ...contentStyle}}
                                >
                                    <Text
                                        className="recharts-tooltip-label"
                                        style={{...labelStyle}}
                                    >
                                        {name}: {formatNumber(safeValue)} ({pct})
                                    </Text>
                                </div>
                            )
                        }}
                    />
                    <Legend
                        align="left"
                        layout="vertical"
                        verticalAlign="middle"
                        iconSize={layout === 'sideBySide' ? 12 : 14}
                        formatter={(value: string) => {
                            const count = pieData.find((d) => d.name === value)?.value ?? 0
                            return `${value} (${engagementPercentLabel(count, pieGrandTotal)})`
                        }}
                        wrapperStyle={{
                            fontSize: 'var(--mantine-font-size-sm)',
                            lineHeight: 1.25,
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </Box>
    )

    const sharedStyles = (
        <style>
            {`
                .capacity-engagement-pie-wrap .recharts-wrapper,
                .capacity-engagement-pie-wrap .recharts-surface,
                .capacity-engagement-pie-wrap svg {
                    overflow: visible !important;
                }
                .capacity-engagement-pie-wrap .recharts-legend-item {
                    margin-right: 0 !important;
                    white-space: nowrap;
                }
                .capacity-engagement-pie-wrap .recharts-legend-item:not(:last-child) {
                    margin-bottom: var(--mantine-spacing-xs) !important;
                }
                .capacity-matrix-clickable-cell:hover {
                    background-color: var(--mantine-color-gray-1) !important;
                }
            `}
        </style>
    )

    const tableEl = (
        <Table mt={layout === 'stack' ? 'md' : 0} striped layout="auto" w="100%">
            <Table.Thead>
                <Table.Tr>
                    <Table.Th style={{fontSize: 'var(--mantine-font-size-xs)'}}>
                        Capacity Bucket
                    </Table.Th>
                    {ENGAGEMENT_LEVELS.map((eng) => (
                        <Table.Th
                            key={eng}
                            style={{
                                textAlign: 'right',
                                fontSize: 'var(--mantine-font-size-xs)',
                                lineHeight: 1.25,
                                wordBreak: 'break-word',
                            }}
                        >
                            {eng}
                        </Table.Th>
                    ))}
                    <Table.Th
                        style={{
                            textAlign: 'right',
                            fontSize: 'var(--mantine-font-size-xs)',
                        }}
                    >
                        Total
                    </Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {data.rows.map((row) => (
                    <Table.Tr key={row.capacityBucket}>
                        <Table.Td
                            className={
                                onProspectFilter ? 'capacity-matrix-clickable-cell' : undefined
                            }
                            onClick={
                                onProspectFilter
                                    ? () =>
                                          onProspectFilter({
                                              capacityBucket: row.capacityBucket,
                                          })
                                    : undefined
                            }
                            style={onProspectFilter ? {cursor: 'pointer'} : undefined}
                        >
                            {row.capacityBucket}
                        </Table.Td>
                        {ENGAGEMENT_LEVELS.map((eng) => (
                            <TableCell
                                key={eng}
                                engagementLevel={eng}
                                capacityBucket={row.capacityBucket}
                                value={row[eng] || 0}
                                onClick={
                                    onProspectFilter
                                        ? () =>
                                              onProspectFilter({
                                                  capacityBucket: row.capacityBucket,
                                                  engagementLevel: eng,
                                              })
                                        : undefined
                                }
                                style={{
                                    maxWidth: MAX_COLUMN_WIDTH_BY_ENGAGEMENT_LEVEL[eng],
                                    width: 'max-content',
                                }}
                            />
                        ))}
                        <TableCell
                            capacityBucket={row.capacityBucket}
                            value={row.total}
                            onClick={
                                onProspectFilter
                                    ? () => onProspectFilter({capacityBucket: row.capacityBucket})
                                    : undefined
                            }
                        />
                    </Table.Tr>
                ))}
            </Table.Tbody>
            <Table.Tfoot>
                <Table.Tr>
                    <Table.Td>
                        <strong>Grand Total</strong>
                    </Table.Td>
                    {ENGAGEMENT_LEVELS.map((eng) => (
                        <TableCell
                            key={eng}
                            engagementLevel={eng}
                            value={data.rows.reduce((sum, r) => sum + (r[eng] || 0), 0)}
                            onClick={
                                onProspectFilter
                                    ? () => onProspectFilter({engagementLevel: eng})
                                    : undefined
                            }
                            style={{
                                maxWidth: MAX_COLUMN_WIDTH_BY_ENGAGEMENT_LEVEL[eng],
                                width: 'max-content',
                            }}
                        />
                    ))}
                    <Table.Td style={{textAlign: 'right'}}>
                        <strong>{formatNumber(data.grandTotal)}</strong>
                    </Table.Td>
                </Table.Tr>
            </Table.Tfoot>
        </Table>
    )

    if (content === 'chart') {
        return (
            <>
                <Center w="100%">{pieChart}</Center>
                {sharedStyles}
            </>
        )
    }

    if (content === 'table') {
        return (
            <>
                {tableEl}
                {sharedStyles}
            </>
        )
    }

    if (layout === 'sideBySide') {
        return (
            <>
                <Group align="stretch" gap="lg" wrap="nowrap">
                    <Box
                        style={{
                            flex: '1 1 0',
                            minWidth: 0,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {pieChart}
                    </Box>
                    <Box style={{flex: '2 1 0', minWidth: 0, overflow: 'auto'}}>{tableEl}</Box>
                </Group>
                {sharedStyles}
            </>
        )
    }

    return (
        <>
            <Center w="100%">{pieChart}</Center>
            {sharedStyles}
            {tableEl}
        </>
    )
}

export function CapacityEngagementMatrixExportContent({
    data,
    kind,
}: {
    data: CapacityEngagementMatrixData
    kind: ExportKind
}) {
    if (kind === 'table') {
        return <CapacityEngagementMatrixChartAndTable data={data} layout="stack" content="table" />
    }
    return <CapacityEngagementMatrixChartAndTable data={data} layout="stack" content="chart" />
}

export default function CapacityEngagementMatrix({data, title, onProspectFilter}: Props) {
    return (
        <SectionCard title={title}>
            <CapacityEngagementMatrixChartAndTable
                data={data}
                onProspectFilter={onProspectFilter}
            />
        </SectionCard>
    )
}
